import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Switch,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import {
  Inventory as AssetsIcon,
  List as RequirementListIcon,
  Cloud as ServicesIcon,
  Description as DocumentationIcon,
  Gavel as DecisionsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import { format } from 'date-fns';
import HipaaCuiServices from './HipaaCuiServices';
import HipaaCuiDocumentation from './HipaaCuiDocumentation';
import HipaaCuiDecisions from './HipaaCuiDecisions';

const darkGreenColor = "#1D4434";

/**
 * HIPAA/CUI Assets - Shows assets tracking for HIPAA/CUI compliance
 * @returns {JSX.Element} HipaaCuiAssets component
 */
// Map subTab names to indices (Decisions first as default)
const subTabMap = {
  'decisions': 0,
  'requirements': 1,
  'services': 2,
  'documentation': 3
};

const HipaaCuiAssets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subTabParam = searchParams.get('subTab');
  // Default to Decisions (0) when no subTab in URL
  const initialSubTab = subTabParam && subTabMap[subTabParam] !== undefined ? subTabMap[subTabParam] : 0;
  
  const [subTabValue, setSubTabValue] = useState(initialSubTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [assetsProgress, setAssetsProgress] = useState({
    decisions: { total: 0, completed: 0, rate: 0 },
    requirements: { total: 0, inScope: 0, rate: 0 },
    services: { total: 0, inScope: 0, rate: 0 }
  });
  const [progressLoading, setProgressLoading] = useState(true);
  const [requirementFilters, setRequirementFilters] = useState({
    type: '',
    inScope: '',
    delivery: ''
  });
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState(null);
  
  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [formData, setFormData] = useState({
    functionality: '',
    type: '',
    inScope: 'In-Scope',
    comments: '',
    delivery: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (subTabParam && subTabMap[subTabParam] !== undefined) {
      setSubTabValue(subTabMap[subTabParam]);
    }
  }, [subTabParam]);

  const loadAssetsProgress = useCallback(async () => {
    try {
      setProgressLoading(true);
      const [decRes, reqRes, svcRes] = await Promise.all([
        get('/api/hipaa-cui/decisions'),
        get('/api/hipaa-cui/requirements'),
        get('/api/hipaa-cui/services')
      ]);
      const decisions = decRes.success ? (decRes.data || []) : [];
      const requirements = reqRes.success ? (reqRes.data || []) : [];
      const services = svcRes.success ? (svcRes.data || []) : [];
      const completedDec = decisions.filter(d => d.status === 'Completed').length;
      const inScopeReq = requirements.filter(r => r.inScope === 'In-Scope').length;
      const inScopeSvc = services.filter(s => s.inScope === 'In-Scope').length;
      setAssetsProgress({
        decisions: {
          total: decisions.length,
          completed: completedDec,
          rate: decisions.length > 0 ? Math.round((completedDec / decisions.length) * 100) : 0
        },
        requirements: {
          total: requirements.length,
          inScope: inScopeReq,
          rate: requirements.length > 0 ? Math.round((inScopeReq / requirements.length) * 100) : 0
        },
        services: {
          total: services.length,
          inScope: inScopeSvc,
          rate: services.length > 0 ? Math.round((inScopeSvc / services.length) * 100) : 0
        }
      });
    } catch (err) {
      console.error('Error loading assets progress:', err);
    } finally {
      setProgressLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssetsProgress();
  }, [loadAssetsProgress]);

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
    const currentParams = new URLSearchParams(searchParams);
    const subTabName = Object.keys(subTabMap).find(key => subTabMap[key] === newValue);
    if (subTabName) {
      currentParams.set('subTab', subTabName);
    } else {
      currentParams.delete('subTab');
    }
    setSearchParams(currentParams, { replace: true });
  };

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const response = await get('/api/hipaa-cui/requirements');
      
      if (response.success) {
        // Convert any boolean inScope values to string format for backward compatibility
        // Use a more efficient approach - only convert if needed
        const data = response.data || [];
        const convertedRequirements = data.length > 0 && typeof data[0].inScope === 'boolean'
          ? data.map(req => ({
              ...req,
              inScope: req.inScope ? 'In-Scope' : 'Out-of-Scope'
            }))
          : data;
        setRequirements(convertedRequirements);
      }
    } catch (err) {
      console.error('Error loading HIPAA/CUI requirements:', err);
      setError(err.message || 'Failed to load requirements data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const applyRequirementFilters = useCallback(() => {
    let filtered = [...requirements];
    
    if (requirementFilters.type) {
      filtered = filtered.filter(r => r.type === requirementFilters.type);
    }
    if (requirementFilters.inScope) {
      filtered = filtered.filter(r => r.inScope === requirementFilters.inScope);
    }
    if (requirementFilters.delivery) {
      filtered = filtered.filter(r => r.delivery === requirementFilters.delivery);
    }
    
    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField] || '';
        let bValue = b[sortField] || '';
        
        // For inScope, normalize values for proper sorting
        if (sortField === 'inScope') {
          // Sort order: In-Scope, In Scope When available, Out-of-Scope
          const order = { 'In-Scope': 1, 'In Scope When available': 2, 'Out-of-Scope': 3 };
          aValue = order[aValue] || 999;
          bValue = order[bValue] || 999;
        } else {
          // String comparison for other fields
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredRequirements(filtered);
  }, [requirements, requirementFilters, sortField, sortOrder]);

  useEffect(() => {
    // Only load data when Requirement List tab is active (index 1 after Decisions first)
    if (subTabValue === 1) {
      loadData();
    }
  }, [subTabValue]);

  useEffect(() => {
    applyRequirementFilters();
  }, [applyRequirementFilters]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };


  const handleOpenDialog = (requirement = null) => {
    if (requirement) {
      setEditingRequirement(requirement);
      setFormData({
        functionality: requirement.functionality || '',
        type: requirement.type || '',
        inScope: requirement.inScope || 'In-Scope',
        comments: requirement.comments || '',
        delivery: requirement.delivery || ''
      });
    } else {
      setEditingRequirement(null);
      setFormData({
        functionality: '',
        type: '',
        inScope: 'In-Scope',
        comments: '',
        delivery: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRequirement(null);
    setFormData({
      functionality: '',
      type: '',
      inScope: 'In-Scope',
      comments: '',
      delivery: ''
    });
  };

  const handleSaveRequirement = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.functionality.trim() || !formData.type) {
        setError('Functionality and Type are required');
        setSaving(false);
        return;
      }

      let response;
      if (editingRequirement) {
        response = await authPut(`/api/hipaa-cui/requirements/${editingRequirement._id}`, formData);
      } else {
        response = await authPost('/api/hipaa-cui/requirements', formData);
      }

      if (response.success) {
        handleCloseDialog();
        await loadData(false); // Reload requirements without showing loading spinner
      } else {
        setError(response.message || 'Failed to save requirement');
      }
    } catch (err) {
      console.error('Error saving requirement:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save requirement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequirement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this requirement?')) {
      return;
    }

    try {
      setDeleting(id);
      setError(null);

      const response = await authDelete(`/api/hipaa-cui/requirements/${id}`);

      if (response.success) {
        await loadData(false); // Reload requirements without showing loading spinner
      } else {
        setError(response.message || 'Failed to delete requirement');
      }
    } catch (err) {
      console.error('Error deleting requirement:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete requirement');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadJson = () => {
    try {
      // Filter out featureId and epicId, and convert boolean inScope to string format
      const cleanedRequirements = requirements.map(({ featureId, epicId, inScope, ...rest }) => {
        // Convert boolean inScope values to string format
        let convertedInScope = inScope;
        if (typeof inScope === 'boolean') {
          convertedInScope = inScope ? 'In-Scope' : 'Out-of-Scope';
        } else if (!inScope || (inScope !== 'In-Scope' && inScope !== 'Out-of-Scope' && inScope !== 'In Scope When available')) {
          // Default to 'In-Scope' if invalid value
          convertedInScope = 'In-Scope';
        }
        
        return {
          ...rest,
          inScope: convertedInScope
        };
      });
      
      const dataStr = JSON.stringify(cleanedRequirements, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hipaa-cui-requirements-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading JSON:', err);
      setError('Failed to download JSON file');
    }
  };

  const handleDownloadReport = () => {
    try {
      if (filteredRequirements.length === 0) {
        setError('No requirements to export');
        return;
      }

      const printWindow = window.open('', '_blank');
      const printDate = new Date();
      const formattedDate = format(printDate, 'MMMM dd, yyyy');
      const formattedTime = format(printDate, 'hh:mm a');
      
      // Calculate statistics
      const totalRequirements = filteredRequirements.length;
      const inScopeCount = filteredRequirements.filter(r => r.inScope === 'In-Scope').length;
      const outOfScopeCount = filteredRequirements.filter(r => r.inScope === 'Out-of-Scope').length;
      const inScopeRate = totalRequirements > 0 ? ((inScopeCount / totalRequirements) * 100).toFixed(1) : 0;
      
      // Count by type
      const highQCoreCount = filteredRequirements.filter(r => r.type === 'HighQ Core').length;
      const aiIntegrationCount = filteredRequirements.filter(r => r.type === 'AI Integration').length;
      const thirdPartyCount = filteredRequirements.filter(r => r.type === '3rd Party Integration').length;
      const otherTypeCount = totalRequirements - highQCoreCount - aiIntegrationCount - thirdPartyCount;
      
      // Count by delivery
      const q2Count = filteredRequirements.filter(r => r.delivery === 'Q2-2026').length;
      const q3Count = filteredRequirements.filter(r => r.delivery === 'Q3-2026').length;
      const naCount = filteredRequirements.filter(r => r.delivery === 'N/A').length;
      const tbdCount = filteredRequirements.filter(r => r.delivery === 'TBD').length;
      
      const escapeHtml = (text) => {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>HIPAA/CUI Requirements Report</title>
          <meta charset="UTF-8">
          <style>
            @page {
              margin: 2cm 1.5cm;
              size: A4;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              .page-break {
                page-break-before: always;
              }
              thead {
                display: table-header-group;
              }
              tfoot {
                display: table-footer-group;
              }
              tr {
                page-break-inside: avoid;
              }
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: 'Calibri', 'Arial', 'Helvetica', sans-serif;
              font-size: 11pt;
              color: #2c3e50;
              line-height: 1.5;
              margin: 0;
              padding: 0;
              background: #ffffff;
            }
            .header {
              background: linear-gradient(135deg, #1D4434 0%, #2d5a47 100%);
              color: white;
              padding: 25px 30px;
              margin-bottom: 30px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .header-content {
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-wrap: wrap;
            }
            .header-title {
              font-size: 24pt;
              font-weight: 700;
              margin: 0 0 8px 0;
              letter-spacing: 0.5px;
            }
            .header-subtitle {
              font-size: 11pt;
              opacity: 0.9;
              margin: 0;
            }
            .header-meta {
              text-align: right;
              font-size: 10pt;
              opacity: 0.9;
            }
            .executive-summary {
              background: #f8f9fa;
              border: 2px solid #1D4434;
              border-radius: 8px;
              padding: 25px;
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .executive-summary h2 {
              color: #1D4434;
              font-size: 18pt;
              font-weight: 700;
              margin: 0 0 20px 0;
              padding-bottom: 10px;
              border-bottom: 2px solid #1D4434;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-card {
              background: white;
              border-left: 4px solid #1D4434;
              padding: 15px;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            .stat-label {
              font-size: 10pt;
              color: #666;
              margin-bottom: 8px;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .stat-value {
              font-size: 24pt;
              font-weight: 700;
              color: #1D4434;
            }
            .stat-card.success .stat-value {
              color: #2e7d32;
            }
            .stat-card.info .stat-value {
              color: #0288d1;
            }
            .stat-card.primary .stat-value {
              color: #1976d2;
            }
            .stat-card.secondary .stat-value {
              color: #9c27b0;
            }
            .stat-card.warning .stat-value {
              color: #ed6c02;
            }
            .progress-section {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
            }
            .progress-bar-container {
              background: #e0e0e0;
              border-radius: 10px;
              height: 25px;
              margin: 10px 0;
              overflow: hidden;
            }
            .progress-bar {
              background: linear-gradient(90deg, #2e7d32 0%, #4caf50 100%);
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              font-size: 10pt;
              transition: width 0.3s ease;
            }
            .section-title {
              font-size: 16pt;
              font-weight: 700;
              color: #1D4434;
              margin: 30px 0 15px 0;
              padding-bottom: 8px;
              border-bottom: 2px solid #1D4434;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              page-break-inside: auto;
            }
            thead {
              background: linear-gradient(135deg, #1D4434 0%, #2d5a47 100%);
              color: white;
            }
            th {
              padding: 14px 10px;
              text-align: left;
              font-weight: 600;
              font-size: 10pt;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid rgba(255,255,255,0.2);
            }
            td {
              padding: 12px 10px;
              border: 1px solid #e0e0e0;
              vertical-align: top;
              word-wrap: break-word;
              font-size: 10pt;
            }
            tbody tr {
              transition: background-color 0.2s;
            }
            tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            tbody tr:hover {
              background-color: #e8f5e9;
            }
            .badge {
              display: inline-block;
              padding: 5px 12px;
              border-radius: 15px;
              font-size: 9pt;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              white-space: nowrap;
            }
            .badge-primary { background-color: #1976d2; color: white; }
            .badge-secondary { background-color: #9c27b0; color: white; }
            .badge-warning { background-color: #ed6c02; color: white; }
            .badge-info { background-color: #0288d1; color: white; }
            .badge-success { background-color: #2e7d32; color: white; }
            .badge-default { background-color: #757575; color: white; }
            .comment-cell {
              max-width: 250px;
              word-wrap: break-word;
              white-space: pre-wrap;
              line-height: 1.4;
            }
            .title-cell {
              font-weight: 600;
              color: #1D4434;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #1D4434;
              text-align: center;
              font-size: 9pt;
              color: #666;
            }
            .text-center {
              text-align: center;
            }
            .text-right {
              text-align: right;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-content">
              <div>
                <div class="header-title">HIPAA/CUI Requirements Report</div>
                <div class="header-subtitle">Compliance Management & Requirements Tracking</div>
              </div>
              <div class="header-meta">
                <div><strong>Report Date:</strong> ${formattedDate}</div>
                <div><strong>Generated:</strong> ${formattedTime}</div>
              </div>
            </div>
          </div>

          <!-- Executive Summary -->
          <div class="executive-summary">
            <h2>Executive Summary</h2>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">Total Requirements</div>
                <div class="stat-value">${totalRequirements}</div>
              </div>
              <div class="stat-card success">
                <div class="stat-label">In-Scope</div>
                <div class="stat-value">${inScopeCount}</div>
              </div>
              <div class="stat-card info">
                <div class="stat-label">Out-of-Scope</div>
                <div class="stat-value">${outOfScopeCount}</div>
              </div>
              <div class="stat-card primary">
                <div class="stat-label">HighQ Core</div>
                <div class="stat-value">${highQCoreCount}</div>
              </div>
              <div class="stat-card secondary">
                <div class="stat-label">AI Integration</div>
                <div class="stat-value">${aiIntegrationCount}</div>
              </div>
              <div class="stat-card warning">
                <div class="stat-label">3rd Party Integration</div>
                <div class="stat-value">${thirdPartyCount}</div>
              </div>
            </div>
            <div class="progress-section">
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <span style="font-weight: 600; color: #1D4434;">In-Scope Rate</span>
                <span style="font-weight: 600; color: #1D4434;">${inScopeRate}%</span>
              </div>
              <div class="progress-bar-container">
                <div class="progress-bar" style="width: ${inScopeRate}%;">${inScopeRate}%</div>
              </div>
            </div>
          </div>

          <!-- Detailed Requirements List -->
          <div class="section-title">Requirements Details</div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 5%;" class="text-center">#</th>
                <th style="width: 25%;">Functionality</th>
                <th style="width: 15%;">Type</th>
                <th style="width: 12%;">In-Scope/Out-of-Scope</th>
                <th style="width: 10%;">Delivery</th>
                <th style="width: 18%;">Comments</th>
                <th style="width: 8%;">Last Changed By</th>
                <th style="width: 7%;">Changed Date</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      filteredRequirements.forEach((requirement, index) => {
        const typeClass = 
          requirement.type === 'HighQ Core' ? 'badge-primary' :
          requirement.type === 'AI Integration' ? 'badge-secondary' :
          requirement.type === '3rd Party Integration' ? 'badge-warning' : 'badge-info';
        
        const scopeClass = 
          requirement.inScope === 'In-Scope' ? 'badge-success' :
          requirement.inScope === 'Out-of-Scope' ? 'badge-default' : 'badge-warning';
        
        htmlContent += `
          <tr>
            <td class="text-center">${index + 1}</td>
            <td class="title-cell">${escapeHtml(requirement.functionality || '-')}</td>
            <td><span class="badge ${typeClass}">${escapeHtml(requirement.type || '-')}</span></td>
            <td><span class="badge ${scopeClass}">${escapeHtml(requirement.inScope || 'In-Scope')}</span></td>
            <td>${escapeHtml(requirement.delivery || '-')}</td>
            <td class="comment-cell">${escapeHtml(requirement.comments || '-')}</td>
            <td>${escapeHtml(requirement.updatedBy?.username || '-')}</td>
            <td>${requirement.updatedAt ? format(new Date(requirement.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}</td>
          </tr>
        `;
      });
      
      htmlContent += `
            </tbody>
          </table>
          
          <div class="footer">
            <div><strong>HIPAA/CUI Requirements Report</strong></div>
            <div style="margin-top: 5px;">Generated on ${formattedDate} at ${formattedTime}</div>
            <div style="margin-top: 5px;">This report contains ${totalRequirements} requirement${totalRequirements !== 1 ? 's' : ''} | Confidential Document</div>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog
      setTimeout(() => {
        printWindow.print();
      }, 250);

      setSuccessMessage('Requirements report opened for printing/download as PDF');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error generating requirements report:', err);
      setError('Failed to generate requirements report');
    }
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const handleUploadJson = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      if (!Array.isArray(jsonData)) {
        setError('JSON file must contain an array of requirement objects');
        return;
      }

      const response = await authPost('/api/hipaa-cui/requirements/bulk-import', {
        requirements: jsonData,
        clearExisting: false // Set to true if you want to replace all existing requirements
      });

      if (response.success) {
        const summary = response.summary || {};
        const message = [
          response.message,
          summary.errors && summary.errors.length > 0 && `${summary.errors.length} error(s) occurred`
        ].filter(Boolean).join('. ');
        
        setSuccessMessage(message);
        if (summary.errors && summary.errors.length > 0) {
          setError(`Errors: ${summary.errors.slice(0, 5).join('; ')}${summary.errors.length > 5 ? ' ...' : ''}`);
        }
        await loadData(false); // Reload requirements without showing loading spinner
        setTimeout(() => {
          setSuccessMessage(null);
          setError(null);
        }, 10000);
      } else {
        setError(response.message || 'Failed to import requirements');
      }
    } catch (err) {
      console.error('Error uploading JSON:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload JSON file. Please check the file format.');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const renderRequirementList = () => {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Compact Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Filters and Actions in Same Line */}
        <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={requirementFilters.type}
                  onChange={(e) => setRequirementFilters({ ...requirementFilters, type: e.target.value })}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="HighQ Core">HighQ Core</MenuItem>
                  <MenuItem value="AI Integration">AI Integration</MenuItem>
                  <MenuItem value="3rd Party Integration">3rd Party Integration</MenuItem>
                  <MenuItem value="TR Product Integration">TR Product Integration</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Scope</InputLabel>
                <Select
                  value={requirementFilters.inScope}
                  onChange={(e) => setRequirementFilters({ ...requirementFilters, inScope: e.target.value })}
                  label="Scope"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="In-Scope">In-Scope</MenuItem>
                  <MenuItem value="Out-of-Scope">Out-of-Scope</MenuItem>
                  <MenuItem value="In Scope When available">In Scope When available</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Delivery</InputLabel>
                <Select
                  value={requirementFilters.delivery}
                  onChange={(e) => setRequirementFilters({ ...requirementFilters, delivery: e.target.value })}
                  label="Delivery"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Q2-2026">Q2-2026</MenuItem>
                  <MenuItem value="Q3-2026">Q3-2026</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                  <MenuItem value="TBD">TBD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isAuthenticated() && (
              <Grid item xs={12} sm={6} md={6}>
                <Box display="flex" gap={0.75} flexWrap="wrap" justifyContent={{ xs: 'flex-start', sm: 'flex-start', md: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                      backgroundColor: darkGreenColor,
                      '&:hover': { backgroundColor: '#0f2e1f' }
                    }}
                  >
                    Add
                  </Button>
                  {isAdmin() && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                      component="label"
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                      <input
                        type="file"
                        accept=".json,application/json"
                        hidden
                        onChange={handleUploadJson}
                        disabled={uploading}
                      />
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadJson}
                    disabled={requirements.length === 0}
                  >
                    Download
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadReport}
                    disabled={filteredRequirements.length === 0}
                  >
                    Download Report
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Requirements Table - Full Height */}
        {loading ? (
          <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flex: 1 }}>
            <Box textAlign="center">
              <CircularProgress size={50} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading requirements...
              </Typography>
            </Box>
          </Paper>
        ) : (
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              '& .MuiTable-root': {
                minWidth: 650
              }
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2,
                      '&:first-of-type': {
                        borderTopLeftRadius: '8px'
                      }
                    }}
                    width="80"
                  >
                    Sr. No
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Functionality
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2,
                      '& .MuiTableSortLabel-root': {
                        color: 'white',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.8)'
                        },
                        '& .MuiTableSortLabel-icon': {
                          color: 'rgba(255, 255, 255, 0.7) !important'
                        }
                      }
                    }}
                  >
                    <TableSortLabel
                      active={sortField === 'inScope'}
                      direction={sortField === 'inScope' ? sortOrder : 'asc'}
                      onClick={() => handleSort('inScope')}
                      sx={{ color: 'white' }}
                    >
                      In-Scope/Out-of-Scope
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Delivery
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Comments
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Last Changed By
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Changed Date
                  </TableCell>
                  {isAuthenticated() && (
                    <TableCell 
                      align="right"
                      sx={{ 
                        backgroundColor: 'background.paper',
                        background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        borderBottom: 'none',
                        py: 2,
                        '&:last-of-type': {
                          borderTopRightRadius: '8px'
                        }
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequirements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAuthenticated() ? 9 : 8} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        No requirements found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequirements.map((requirement, index) => (
                    <TableRow 
                      key={requirement._id}
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(29, 68, 52, 0.04)',
                          transform: 'scale(1.001)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        },
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary', py: 2 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: darkGreenColor }}>
                          {requirement.functionality}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip 
                          label={requirement.type} 
                          size="small"
                          sx={{
                            fontWeight: 600,
                            height: '28px',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                          color={
                            requirement.type === 'HighQ Core' ? 'primary' :
                            requirement.type === 'AI Integration' ? 'secondary' :
                            requirement.type === '3rd Party Integration' ? 'warning' : 'info'
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip 
                          label={requirement.inScope || 'In-Scope'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            height: '28px',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                          color={
                            requirement.inScope === 'In-Scope' ? 'success' :
                            requirement.inScope === 'Out-of-Scope' ? 'default' : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {requirement.delivery || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, maxWidth: 300 }}>
                        <Tooltip title={requirement.comments || '-'} arrow>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.secondary'
                            }}
                          >
                            {requirement.comments || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {requirement.updatedBy?.username || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {requirement.updatedAt ? format(new Date(requirement.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}
                        </Typography>
                      </TableCell>
                      {isAuthenticated() && (
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(requirement)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.dark',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRequirement(requirement._id)}
                                disabled={deleting === requirement._id}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'error.dark',
                                    transform: 'scale(1.1)'
                                  },
                                  '&:disabled': {
                                    opacity: 0.5
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {deleting === requirement._id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingRequirement ? 'Edit Requirement' : 'Add Requirement'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Functionality"
                    value={formData.functionality}
                    onChange={(e) => setFormData({ ...formData, functionality: e.target.value })}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      label="Type"
                    >
                      <MenuItem value="HighQ Core">HighQ Core</MenuItem>
                      <MenuItem value="AI Integration">AI Integration</MenuItem>
                      <MenuItem value="3rd Party Integration">3rd Party Integration</MenuItem>
                      <MenuItem value="TR Product Integration">TR Product Integration</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>In-Scope/Out-of-Scope</InputLabel>
                    <Select
                      value={formData.inScope}
                      onChange={(e) => setFormData({ ...formData, inScope: e.target.value })}
                      label="In-Scope/Out-of-Scope"
                    >
                      <MenuItem value="In-Scope">In-Scope</MenuItem>
                      <MenuItem value="Out-of-Scope">Out-of-Scope</MenuItem>
                      <MenuItem value="In Scope When available">In Scope When available</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Delivery</InputLabel>
                    <Select
                      value={formData.delivery}
                      onChange={(e) => setFormData({ ...formData, delivery: e.target.value })}
                      label="Delivery"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Q2-2026">Q2-2026</MenuItem>
                      <MenuItem value="Q3-2026">Q3-2026</MenuItem>
                      <MenuItem value="N/A">N/A</MenuItem>
                      <MenuItem value="TBD">TBD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Comments"
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRequirement}
              variant="contained"
              disabled={saving || !formData.functionality.trim() || !formData.type}
              sx={{
                backgroundColor: darkGreenColor,
                '&:hover': { backgroundColor: '#0f2e1f' }
              }}
            >
              {saving ? 'Saving...' : editingRequirement ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderServices = () => {
    return <HipaaCuiServices />;
  };

  const renderDocumentation = () => {
    return <HipaaCuiDocumentation />;
  };

  const renderDecisions = () => {
    return <HipaaCuiDecisions />;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Progress overview chart */}
      <Card variant="outlined" sx={{ mb: 2, py: 1.5, px: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Assets progress
        </Typography>
        {progressLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" fontWeight={600}>Decisions</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {assetsProgress.decisions.completed}/{assetsProgress.decisions.total} · {assetsProgress.decisions.rate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={assetsProgress.decisions.rate}
                  color="primary"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" fontWeight={600}>Requirements</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {assetsProgress.requirements.inScope}/{assetsProgress.requirements.total} in-scope · {assetsProgress.requirements.rate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={assetsProgress.requirements.rate}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: `${darkGreenColor}20`,
                    '& .MuiLinearProgress-bar': { backgroundColor: darkGreenColor }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" fontWeight={600}>Services</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {assetsProgress.services.inScope}/{assetsProgress.services.total} in-scope · {assetsProgress.services.rate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={assetsProgress.services.rate}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: `${darkGreenColor}20`,
                    '& .MuiLinearProgress-bar': { backgroundColor: darkGreenColor }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Card>

      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Assets Tracking
        </Typography>
        <Tabs
          value={subTabValue}
          onChange={handleSubTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ 
            minHeight: 'auto',
            '& .MuiTab-root': {
              minHeight: 40,
              padding: '6px 16px',
              fontSize: '0.875rem',
              textTransform: 'none',
              fontWeight: 500
            }
          }}
        >
          <Tab icon={<DecisionsIcon sx={{ fontSize: 18 }} />} label="Decisions" iconPosition="start" />
          <Tab icon={<RequirementListIcon sx={{ fontSize: 18 }} />} label="Requirement List" iconPosition="start" />
          <Tab icon={<ServicesIcon sx={{ fontSize: 18 }} />} label="Services" iconPosition="start" />
          <Tab icon={<DocumentationIcon sx={{ fontSize: 18 }} />} label="Documentation" iconPosition="start" />
        </Tabs>
      </Box>

      {subTabValue === 0 && renderDecisions()}
      {subTabValue === 1 && renderRequirementList()}
      {subTabValue === 2 && renderServices()}
      {subTabValue === 3 && renderDocumentation()}
    </Box>
  );
};

export default HipaaCuiAssets;

